import { ChevronRight, ChevronDown } from 'lucide-react';
import { useDroppable } from '@dnd-kit/core';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import clsx from 'clsx';
import type { Category } from '@/types';
import { useAppStore } from '@/store/appStore';
import { InlineEditInput } from './InlineEditInput';

interface CategoryItemProps {
  category: Category;
  isExpanded: boolean;
  onToggle: () => void;
  onCancelCreate?: () => void;
  children: React.ReactNode;
}

export function CategoryItem({
  category,
  isExpanded,
  onToggle,
  onCancelCreate,
  children,
}: CategoryItemProps) {
  const { isOver, setNodeRef: setDropRef } = useDroppable({
    id: `category-drop-${category.id}`,
    data: { type: 'category', categoryId: category.id },
  });

  const {
    attributes,
    listeners,
    setNodeRef: setSortRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: `sortable-cat-${category.id}`,
    data: { type: 'sortable-category', categoryId: category.id },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : undefined,
  };

  // Merge refs: both droppable (for projects) and sortable (for reorder)
  const setNodeRef = (node: HTMLElement | null) => {
    setDropRef(node);
    setSortRef(node);
  };

  const editingItems = useAppStore((state) => state.editingItems);
  const savePendingItemName = useAppStore((state) => state.savePendingItemName);
  const isEditing = editingItems.has(category.id);

  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      {/* Category Header - entire header is the drag handle */}
      <button
        {...(isEditing ? {} : listeners)}
        onClick={isEditing ? undefined : onToggle}
        className={clsx(
          'w-full flex items-center gap-1.5 px-2 py-1.5 rounded-md transition-colors group select-none',
          isEditing ? 'cursor-default' : 'cursor-grab active:cursor-grabbing',
          isOver
            ? 'bg-accent-violet/15 text-text-secondary ring-1 ring-accent-violet/30'
            : 'text-text-tertiary hover:bg-surface-hover hover:text-text-secondary'
        )}
      >
        <span className="text-text-quaternary group-hover:text-text-tertiary transition-colors shrink-0">
          {isExpanded ? (
            <ChevronDown className="w-3 h-3" />
          ) : (
            <ChevronRight className="w-3 h-3" />
          )}
        </span>
        {isEditing ? (
          <InlineEditInput
            value={category.name}
            placeholder="Nome categoria..."
            onSave={(name) => savePendingItemName(category.id, name)}
            onCancel={() => onCancelCreate?.()}
            className="uppercase tracking-wider font-semibold"
          />
        ) : (
          <>
            <span className="flex-1 text-left text-[10px] font-semibold uppercase tracking-wider truncate">
              {category.name}
            </span>
            <span className="text-[10px] text-text-quaternary tabular-nums font-medium">
              {category.taskCount.toLocaleString()}
            </span>
          </>
        )}
      </button>

      {/* Category Children (projects) */}
      {isExpanded && (
        <div className="ml-2 pl-1.5 border-l border-border-primary/40 mt-0.5 space-y-0.5">
          {children}
        </div>
      )}
    </div>
  );
}
